import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDataProvider, useNotify } from 'react-admin';

import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Theme } from '@mui/material/styles';

import { FormDivider } from 'shared/components/common/FormDivider.componen';
import { removeTags } from 'shared/components/fields/RichTextFieldWithEllipsis.component';
import { ResourceName } from 'shared/enums/resource-name.enum';
import { useAppPermissions } from 'shared/hooks/useAppPermissions';
import { BPError } from 'shared/services/backoffice-data-provider';
import { isPublisher } from 'shared/services/firebase-auth-provider/checkPermissions';
import {
  CAMPAIGN_LANGUAGE_OPTIONS,
  formatCampaignLanguageLabel,
  getCampaignLanguageDisplayLabel,
  getTargetLanguageOptionsForDialog
} from 'shared/utils/constants/campaign-languages.const';
import { rem, vh } from 'shared/utils/styles/css-unit';

import { CampaignDataProvider } from '../CampaignDataProvider.service';
import { Campaign } from '../models/campaign.model';
import {
  CampaignTranslationDisplayText,
  CampaignTranslationMedia,
  CampaignTranslationRow,
  CampaignTranslationsListPayload
} from '../models/campaign-translation.model';

/** Muted indigo for text-style “add” actions (aligned with reference + ADD pattern). */
const ADD_TRANSLATION_ACTION_COLOR = '#5c6bc0';

/** Matches react-admin / Material-UI v4 default primary used by {@link SaveButton} (indigo). */
function raContainedPrimarySx(theme: Theme) {
  return {
    textTransform: 'uppercase' as const,
    bgcolor: '#3f51b5',
    color: '#fff',
    '&:hover': {
      bgcolor: '#303f9f'
    },
    '&.Mui-disabled': {
      bgcolor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled
    }
  };
}

function emptyDisplayText(): CampaignTranslationDisplayText {
  return {
    headline: '',
    text: '',
    price_action_text: '',
    below_button: '',
    button: ''
  };
}

function rowToForm(row: CampaignTranslationRow) {
  return {
    languageCode: row.language_code,
    name: row.name ?? '',
    description: row.description ?? '',
    display_text: row.display_text
      ? { ...emptyDisplayText(), ...row.display_text }
      : emptyDisplayText(),
    media: row.media,
    source_hash: undefined as string | undefined,
    model: undefined as string | undefined,
    token_usage: undefined
  };
}

function buildTranslationMedia(
  sourceMedia: Array<{ url?: string; type?: string }> | undefined,
  translated?: CampaignTranslationMedia[]
): CampaignTranslationMedia[] {
  const source = (sourceMedia ?? []).filter((m) => m?.url);
  return source.map((item, i) => ({
    url: item.url!,
    type: item.type ?? 'image',
    text:
      translated?.[i]?.text ??
      translated?.find((t) => t.url === item.url)?.text ??
      ''
  }));
}

export interface CampaignTranslationsSectionProps {
  /** Passed explicitly so this works outside SimpleForm (same record as the edit view). */
  record?: Campaign | null;
}

export function CampaignTranslationsSection(props: CampaignTranslationsSectionProps) {
  const { record } = props;
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { permissions } = useAppPermissions();
  const canPublish = isPublisher(permissions?.monetization);

  const campaignDp = dataProvider as InstanceType<typeof CampaignDataProvider>;

  const [listPayload, setListPayload] =
    useState<CampaignTranslationsListPayload | null>(null);
  const [listLoading, setListLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExisting, setEditingExisting] = useState(false);
  const [languageCode, setLanguageCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [displayText, setDisplayText] = useState<CampaignTranslationDisplayText>(
    emptyDisplayText()
  );
  const [translationMedia, setTranslationMedia] = useState<
    CampaignTranslationMedia[]
  >([]);
  const [sourceHash, setSourceHash] = useState<string | undefined>();
  const [modelFromAi, setModelFromAi] = useState<string | undefined>();
  const [tokenUsage, setTokenUsage] = useState<
    { input_tokens?: number; output_tokens?: number; total_tokens?: number } | undefined
  >();

  const [sourceLang, setSourceLang] = useState('en');
  const [tone, setTone] = useState('neutral');

  const [recommendLoading, setRecommendLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const campaignId = record?.id;
  const sourceMedia = Array.isArray(record?.media) ? record?.media ?? [] : [];

  const existingLanguageCodes = useMemo(
    () => listPayload?.translations?.map((row) => row.language_code) ?? [],
    [listPayload?.translations]
  );

  const targetLanguageOptions = useMemo(
    () =>
      getTargetLanguageOptionsForDialog(
        existingLanguageCodes,
        languageCode,
        editingExisting
      ),
    [existingLanguageCodes, languageCode, editingExisting]
  );

  const loadList = useCallback(async () => {
    if (!campaignId) return;
    setListLoading(true);
    try {
      const { data } = await campaignDp.getCampaignTranslationsList(
        ResourceName.CAMPAIGNS,
        { campaignId }
      );
      setListPayload(data);
    } catch (e) {
      notify((e as BPError)?.message ?? 'Failed to load translations.', {
        type: 'error'
      });
    } finally {
      setListLoading(false);
    }
  }, [campaignDp, campaignId, notify]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const openNew = () => {
    setEditingExisting(false);
    setLanguageCode('');
    setName('');
    setDescription('');
    setDisplayText(emptyDisplayText());
    setTranslationMedia(buildTranslationMedia(sourceMedia));
    setSourceHash(undefined);
    setModelFromAi(undefined);
    setTokenUsage(undefined);
    setDialogOpen(true);
  };

  const openEdit = (row: CampaignTranslationRow) => {
    setEditingExisting(true);
    const f = rowToForm(row);
    setLanguageCode(f.languageCode);
    setName(f.name);
    setDescription(f.description);
    setDisplayText(f.display_text);
    setTranslationMedia(buildTranslationMedia(sourceMedia, f.media));
    setSourceHash(undefined);
    setModelFromAi(undefined);
    setTokenUsage(undefined);
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const handleRecommend = async () => {
    if (!campaignId) return;
    const target = languageCode.trim().toLowerCase();
    if (!target) {
      notify('Enter a target language code first (e.g. de, pt-br).', {
        type: 'warning'
      });
      return;
    }
    setRecommendLoading(true);
    try {
      const { data } = await campaignDp.recommendCampaignTranslation(
        ResourceName.CAMPAIGNS,
        {
          campaignId,
          target_lang: target,
          source_lang: sourceLang.trim() || undefined,
          tone: tone.trim() || undefined
        }
      );
      setName(data.name);
      if (data.description != null) setDescription(data.description);
      setDisplayText({ ...emptyDisplayText(), ...data.display_text });
      setTranslationMedia(buildTranslationMedia(sourceMedia, data.media));
      setSourceHash(data.source_hash);
      setModelFromAi(data.model);
      setTokenUsage(data.token_usage);
      notify('Translation suggestion loaded. Review and save.', {
        type: 'success'
      });
    } catch (e) {
      notify(
        (e as BPError)?.message ??
          'Translation suggestion failed. Try again later.',
        { type: 'error' }
      );
    } finally {
      setRecommendLoading(false);
    }
  };

  const handleSave = async () => {
    if (!campaignId || !canPublish) return;
    const lang = languageCode.trim().toLowerCase();
    if (!lang) {
      notify('Language code is required.', { type: 'warning' });
      return;
    }
    if (!name.trim()) {
      notify('Name is required.', { type: 'warning' });
      return;
    }
    setSaveLoading(true);
    try {
      await campaignDp.saveCampaignTranslation(ResourceName.CAMPAIGNS, {
        campaignId,
        languageCode: lang,
        name: name.trim(),
        description: description.trim() || undefined,
        display_text: displayText,
        ...(translationMedia.length > 0 ? { media: translationMedia } : {}),
        ...(sourceHash ? { source_hash: sourceHash } : {}),
        ...(modelFromAi ? { model: modelFromAi } : {}),
        ...(tokenUsage ? { token_usage: tokenUsage } : {})
      });
      notify('Translation saved.', { type: 'success' });
      setDialogOpen(false);
      await loadList();
    } catch (e) {
      notify((e as BPError)?.message ?? 'Failed to save translation.', {
        type: 'error'
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const previewSnippet = useMemo(() => {
    const t = (s: string) => (s || '').replace(/\s+/g, ' ').trim();
    const strip = (s: string) => t(removeTags(s));
    const truncate = (s: string, max: number) =>
      s.length <= max ? s : `${s.slice(0, max).trim()}…`;
    return (row: CampaignTranslationRow) => {
      const textPlain = strip(row.display_text?.text ?? '');
      const parts = [
        strip(row.name ?? ''),
        strip(row.display_text?.headline ?? ''),
        truncate(textPlain, 120)
      ].filter(Boolean);
      return parts.join(' · ') || '—';
    };
  }, []);

  if (!campaignId) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: vh(8),
        pt: rem(3),
        mt: rem(2),
        pb: rem(4),
        px: rem(2)
      }}
    >
      <FormDivider title='Translations' />
      <Typography variant='body2' sx={{ mb: rem(2), color: 'text.secondary' }}>
        Manage saved copy per language. AI suggestion does not persist until you
        save.
      </Typography>

      <Stack direction='row' spacing={2} sx={{ mb: rem(2) }}>
        <Button
          variant='text'
          disabled={listLoading}
          onClick={openNew}
          startIcon={
            <AddCircleOutlinedIcon sx={{ color: ADD_TRANSLATION_ACTION_COLOR }} />
          }
          sx={{
            color: ADD_TRANSLATION_ACTION_COLOR,
            fontWeight: 500,
            textTransform: 'none',
            px: 0.5,
            '&:hover': {
              bgcolor: 'rgba(92, 107, 192, 0.08)'
            }
          }}
        >
          Add translation
        </Button>
      </Stack>

      <TableContainer
        component={Paper}
        variant='outlined'
        sx={(theme) => ({
          bgcolor: theme.palette.grey[50],
          borderRadius: 1,
          p: rem(1)
        })}
      >
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell sx={{ borderBottomWidth: 1 }}>
                <Typography variant='subtitle2' component='span' color='text.secondary'>
                  Language
                </Typography>
              </TableCell>
              <TableCell sx={{ borderBottomWidth: 1 }}>
                <Typography variant='subtitle2' component='span' color='text.secondary'>
                  Preview
                </Typography>
              </TableCell>
              <TableCell align='right' sx={{ borderBottomWidth: 1, width: rem(8) }}>
                {' '}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {listLoading && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant='body2'>Loading…</Typography>
                </TableCell>
              </TableRow>
            )}
            {!listLoading &&
              (listPayload?.translations?.length ?? 0) === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant='body2'>
                      No translations yet. Add one to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            {!listLoading &&
              listPayload?.translations?.map((row) => (
                <TableRow key={row.language_code} hover>
                  <TableCell>
                    <Typography variant='body2' component='span'>
                      {getCampaignLanguageDisplayLabel(row.language_code)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: rem(48) }}>
                    <Typography
                      variant='body2'
                      noWrap
                      title={previewSnippet(row)}
                    >
                      {previewSnippet(row)}
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>
                    <Button
                      size='small'
                      variant='text'
                      color='primary'
                      onClick={() => openEdit(row)}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          {editingExisting
            ? `Edit translation (${languageCode})`
            : 'New translation'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label='Language'
              value={languageCode}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setLanguageCode(e.target.value)}
              disabled={editingExisting}
              helperText='BCP-47 code is saved (e.g. de, pt-br)'
              fullWidth
              SelectProps={{ displayEmpty: true }}
            >
              {!editingExisting && (
                <MenuItem value=''>
                  <em>Select language…</em>
                </MenuItem>
              )}
              {targetLanguageOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {formatCampaignLanguageLabel(option)}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label='Source language (optional)'
                value={sourceLang}
                disabled
                onChange={(e) => setSourceLang(e.target.value)}
                fullWidth
              >
                {CAMPAIGN_LANGUAGE_OPTIONS.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {formatCampaignLanguageLabel(option)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label='Tone (optional)'
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                fullWidth
              />
            </Stack>
            <Button
              variant='outlined'
              onClick={() => void handleRecommend()}
              disabled={recommendLoading}
            >
              {recommendLoading ? 'Suggesting…' : 'Suggest translation (AI)'}
            </Button>

            <Typography variant='subtitle2'>Details</Typography>
            <TextField
              label='Name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label='Description'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />

            <Typography variant='subtitle2'>Display text</Typography>
            <TextField
              label='Headline'
              value={displayText.headline}
              onChange={(e) =>
                setDisplayText((d) => ({ ...d, headline: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label='Text'
              value={displayText.text}
              onChange={(e) =>
                setDisplayText((d) => ({ ...d, text: e.target.value }))
              }
              fullWidth
              multiline
              minRows={4}
            />
            <TextField
              label='Price action text'
              value={displayText.price_action_text}
              onChange={(e) =>
                setDisplayText((d) => ({
                  ...d,
                  price_action_text: e.target.value
                }))
              }
              fullWidth
            />
            <TextField
              label='CTA'
              value={displayText.button}
              onChange={(e) =>
                setDisplayText((d) => ({ ...d, button: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label='Below button'
              value={displayText.below_button}
              onChange={(e) =>
                setDisplayText((d) => ({ ...d, below_button: e.target.value }))
              }
              fullWidth
            />

            <Typography variant='subtitle2'>Media captions</Typography>
            {translationMedia.length === 0 ? (
              <Typography variant='body2' color='text.secondary'>
                No media on source campaign.
              </Typography>
            ) : (
              translationMedia.map((item, index) => (
                <Stack
                  key={item.url}
                  direction='row'
                  spacing={2}
                  alignItems='flex-start'
                >
                  <Box
                    component='img'
                    src={item.url}
                    alt=''
                    sx={{
                      width: 80,
                      height: 80,
                      objectFit: 'cover',
                      borderRadius: 1,
                      flexShrink: 0
                    }}
                  />
                  <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant='caption' color='text.secondary'>
                      {item.type}
                    </Typography>
                    <TextField
                      label={`Caption (${index + 1})`}
                      value={item.text ?? ''}
                      onChange={(e) =>
                        setTranslationMedia((prev) =>
                          prev.map((m, i) =>
                            i === index ? { ...m, text: e.target.value } : m
                          )
                        )
                      }
                      fullWidth
                      multiline
                      minRows={2}
                    />
                  </Stack>
                </Stack>
              ))
            )}
          </Stack>
        </DialogContent>
        {!canPublish && (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ px: 3, pb: 1, display: 'block' }}
          >
            Saving translations requires monetization publisher permission.
          </Typography>
        )}
        <DialogActions>
          <Button variant='text' onClick={closeDialog}>
            Cancel
          </Button>
          <Button
            variant='contained'
            disabled={!canPublish || saveLoading}
            onClick={() => void handleSave()}
            startIcon={<SaveIcon />}
            sx={(theme) => raContainedPrimarySx(theme)}
          >
            {saveLoading ? 'Saving…' : 'Save translation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
